import { createApi } from '@reduxjs/toolkit/query/react'
import { User } from '../../models/User'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { removeEmptyItems } from '../../common/util'
import { ConsoleUser } from '../../models/ConsoleUser'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { PendingInvitationFilterModel } from '../../views/ManageTeam/PendingInvitations/PendingInvitationsFilter'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_AUTH_API_BASE_URL!

export interface CsvInviteUser {
  email: string
  firstName: string
  lastName: string
  role: string
  roleIds: string[]
}

export interface CheckedCsvInviteUser extends CsvInviteUser {
  errors: string[]
  warnings: string[]
  inviteStatus: 'already_member' | 'already_invited' | null
}

export interface UserInvitationResult extends CsvInviteUser {
  status: string
  isError: boolean
}

interface BulkInviteRequest extends BaseRequest {
  users: CsvInviteUser[]
}

export interface BulkInviteVerificationResponse {
  users: CheckedCsvInviteUser[]
  generalErrors: string[]
}

export interface BulkInviteResponse {
  users: UserInvitationResult[]
  generalErrors: string[]
}

interface InviteUserRequest extends BaseRequest {
  userData: ConsoleUser
}

interface ResendInviteBody {
  ccMe: boolean
}

interface ResendInviteRequest extends BaseRequest {
  inviteId: string
  body: ResendInviteBody
}

interface InviteDeleteRequest extends BaseRequest {
  inviteId: string
}

export interface InviteResponse {
  id: string
  createdAt: string
  expiresAt?: string
  usedAt?: string
  messageState?: string
  state: string
  user: ConsoleUser
}

export interface OAuth2AuthorizeResponse {
  redirectUri: string
}

export interface LoginToAcademyResponse {
  loginUrl: string
}

export interface OAuth2AuthorizeRequestBody {
  client_id: string
  redirect_uri: string
  response_type: string
  scope: string
  state: string | null
}

export interface OAuth2AuthorizeRequest extends BaseRequest {
  body: OAuth2AuthorizeRequestBody
}

export interface GetOAuth2AppsRequest extends BaseRequest {
  clientId: string
}

export interface OAuth2App {
  clientId: string
  name: string
  imageUrl: string
  description: string
}

export interface GetInvitesRequest extends BasePageRequest {
  filter: PendingInvitationFilterModel
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['invites', 'apps'],
  endpoints: (builder) => ({
    getInvites: builder.query<BasePageResponse<InviteResponse[]>, GetInvitesRequest>({
      query: (request) => {
        return {
          url: `/v2/accounts/${request.accountId}/invite/filter`,
          method: 'POST',
          params: { page: request.pageRequest.page, size: request.pageRequest.size },
          body: {
            filter: request.filter,
          },
        }
      },
      providesTags: ['invites'],
    }),
    inviteUser: builder.mutation<BaseResponse<User>, InviteUserRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite`,
        method: 'POST',
        body: removeEmptyItems(request.userData),
      }),
      invalidatesTags: ['invites'],
    }),
    bulkInviteVerification: builder.mutation<BulkInviteVerificationResponse, BulkInviteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/bulk-verify`,
        method: 'POST',
        body: { users: request.users },
      }),
    }),
    bulkInvite: builder.mutation<BulkInviteResponse, BulkInviteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/bulk-invite`,
        method: 'POST',
        body: { users: request.users },
      }),
      invalidatesTags: ['invites'],
    }),
    resendInvite: builder.mutation<BaseResponse<User>, ResendInviteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/${request.inviteId}/resend`,
        method: 'POST',
        body: request.body,
      }),
      invalidatesTags: ['invites'],
    }),
    deleteInvite: builder.mutation<BaseResponse<User>, InviteDeleteRequest>({
      query: (request) => ({
        url: `/v2/accounts/${request.accountId}/invite/${request.inviteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['invites'],
    }),
    oauth2Authorize: builder.mutation<BaseResponse<OAuth2AuthorizeResponse>, OAuth2AuthorizeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/oauth2/authorize`,
        method: 'POST',
        body: request.body,
      }),
      invalidatesTags: ['apps'],
    }),
    getOAuth2Apps: builder.query<BaseResponse<OAuth2App>, GetOAuth2AppsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/oauth2/apps/${request.clientId}`,
        method: 'GET',
      }),
      providesTags: ['apps'],
    }),
    loginToAcademy: builder.mutation<BaseResponse<LoginToAcademyResponse>, null>({
      query: () => ({
        url: `/v2/console/login-to-academy`,
        method: 'POST',
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useInviteUserMutation,
  useGetInvitesQuery,
  useLazyGetInvitesQuery,
  useDeleteInviteMutation,
  useResendInviteMutation,
  useBulkInviteVerificationMutation,
  useBulkInviteMutation,
  useLazyGetOAuth2AppsQuery,
  useOauth2AuthorizeMutation,
  useLoginToAcademyMutation,
} = authApi
